import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Phone, Mail, User, Calendar, Building, Tag,
  MessageSquare, PhoneCall, Users, FileText, Plus, Trash2,
  CheckCircle, XCircle, Clock, TrendingUp, MapPin, Edit2, Save, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new:         { label: 'Нов',          color: 'bg-blue-100 text-blue-800',   icon: '🔵' },
  active:      { label: 'Активен',      color: 'bg-yellow-100 text-yellow-800', icon: '🟡' },
  client:      { label: 'Клиент',       color: 'bg-green-100 text-green-800',  icon: '🟢' },
  lost:        { label: 'Изгубен',      color: 'bg-red-100 text-red-800',      icon: '🔴' },
  no_response: { label: 'Неотговарящ',  color: 'bg-gray-100 text-gray-600',    icon: '⚫' },
};

const ACTIVITY_TYPES = {
  note:     { label: 'Бележка',  icon: FileText,      color: 'bg-gray-100 text-gray-700' },
  call:     { label: 'Обаждане', icon: PhoneCall,     color: 'bg-blue-100 text-blue-700' },
  meeting:  { label: 'Среща',    icon: Users,         color: 'bg-purple-100 text-purple-700' },
  email:    { label: 'Имейл',    icon: Mail,          color: 'bg-indigo-100 text-indigo-700' },
  viewing:  { label: 'Оглед',    icon: Building,      color: 'bg-green-100 text-green-700' },
  inquiry:  { label: 'Запитване',icon: MessageSquare, color: 'bg-orange-100 text-orange-700' },
};

const OUTCOMES = {
  interested:     '✅ Заинтересован',
  not_interested: '❌ Не е заинтересован',
  callback:       '📞 Ще се обади',
  no_answer:      '📵 Не отговаря',
  closed:         '🤝 Затворена сделка',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Току що';
  if (mins < 60) return `преди ${mins} мин`;
  if (hours < 24) return `преди ${hours} ч`;
  if (days < 30) return `преди ${days} дни`;
  return new Date(dateStr).toLocaleDateString('bg-BG');
}

export default function ContactProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingActivity, setAddingActivity] = useState(false);
  const [activityForm, setActivityForm] = useState({ type: 'note', content: '', outcome: '' });
  const [editingStatus, setEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => { fetchTimeline(); }, [id]);

  const fetchTimeline = async () => {
    try {
      const r = await axios.get(`${API}/contacts/${id}/timeline`);
      setData(r.data);
      setNewStatus(r.data.contact.status || 'new');
    } catch { toast.error('Грешка при зареждане'); }
    finally { setLoading(false); }
  };

  const addActivity = async () => {
    if (!activityForm.content.trim()) return;
    try {
      await axios.post(`${API}/contacts/${id}/activities`, activityForm);
      toast.success('Активността е добавена');
      setAddingActivity(false);
      setActivityForm({ type: 'note', content: '', outcome: '' });
      fetchTimeline();
    } catch { toast.error('Грешка'); }
  };

  const deleteActivity = async (activityId) => {
    if (!window.confirm('Изтриване?')) return;
    try {
      await axios.delete(`${API}/contacts/${id}/activities/${activityId}`);
      fetchTimeline();
    } catch { toast.error('Грешка'); }
  };

  const updateStatus = async () => {
    try {
      await axios.patch(`${API}/contacts/${id}/status`, { status: newStatus });
      toast.success('Статусът е обновен');
      setEditingStatus(false);
      fetchTimeline();
    } catch { toast.error('Грешка'); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="spinner" /></div>;
  if (!data) return <div className="p-6">Контактът не е намерен</div>;

  const { contact, inquiries, activities, stats, broker_name } = data;
  const status = STATUS_CONFIG[contact.status] || STATUS_CONFIG.new;

  // Merge and sort timeline
  const timeline = [
    ...(inquiries || []).map(i => ({ ...i, _type: 'inquiry', _date: i.created_at })),
    ...activities.map(a => ({ ...a, _type: 'activity', _date: a.created_at })),
  ].sort((a, b) => new Date(b._date) - new Date(a._date));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/contacts')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Назад
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Contact Info */}
        <div className="space-y-4">
          {/* Profile card */}
          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 rounded-full bg-[#1B5E37] flex items-center justify-center text-white text-xl font-bold">
                {contact.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex items-center gap-2">
                {editingStatus ? (
                  <>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.icon} {v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" onClick={updateStatus}><Save className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingStatus(false)}><X className="w-3 h-3" /></Button>
                  </>
                ) : (
                  <Badge className={`${status.color} cursor-pointer text-xs`} onClick={() => setEditingStatus(true)}>
                    {status.icon} {status.label} <Edit2 className="w-3 h-3 ml-1 inline" />
                  </Badge>
                )}
              </div>
            </div>

            <h2 className="font-heading font-bold text-xl mb-1">{contact.name}</h2>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Mail className="w-4 h-4" /><a href={`mailto:${contact.email}`} className="text-[#1B5E37] hover:underline">{contact.email}</a></div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4" /><a href={`tel:${contact.phone}`} className="hover:underline">{contact.phone}</a></div>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>Добавен {timeAgo(contact.created_at)}</span></div>
              {broker_name && <div className="flex items-center gap-2"><User className="w-4 h-4" /><span>Брокер: <strong>{broker_name}</strong></span></div>}
              <div className="flex items-center gap-2"><Tag className="w-4 h-4" /><span>Източник: {contact.source || 'сайт'}</span></div>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white border border-border rounded-xl p-5">
            <h3 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wide">Статистика</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{stats.total_inquiries}</div>
                <div className="text-xs text-muted-foreground">Запитвания</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{stats.total_activities}</div>
                <div className="text-xs text-muted-foreground">Активности</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{stats.properties_viewed.length}</div>
                <div className="text-xs text-muted-foreground">Проекта</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-xs font-medium text-purple-600">{timeAgo(stats.last_contact)}</div>
                <div className="text-xs text-muted-foreground">Последен контакт</div>
              </div>
            </div>
          </div>

          {/* Inquired properties */}
          {inquiries.length > 0 && (
            <div className="bg-white border border-border rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wide">Интерес към проекти</h3>
              <div className="space-y-2">
                {[...new Map(inquiries.filter(i => i.property_name).map(i => [i.property_id, i])).values()].map(i => (
                  <div key={i.property_id} className="flex items-center gap-2 text-sm p-2 bg-secondary/30 rounded-lg">
                    <Building className="w-4 h-4 text-[#1B5E37] flex-none" />
                    <span className="font-medium truncate">{i.property_name}</span>
                    <span className="text-xs text-muted-foreground ml-auto flex-none">
                      {inquiries.filter(q => q.property_id === i.property_id).length}x
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Timeline */}
        <div className="lg:col-span-2 space-y-4">
          {/* Add activity */}
          <div className="bg-white border border-border rounded-xl p-5">
            {!addingActivity ? (
              <Button onClick={() => setAddingActivity(true)} className="w-full bg-[#1B5E37] hover:bg-[#154a2c]">
                <Plus className="w-4 h-4 mr-2" /> Добави активност
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  {Object.entries(ACTIVITY_TYPES).filter(([k]) => k !== 'inquiry').map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <button key={key} type="button"
                        onClick={() => setActivityForm(f => ({ ...f, type: key }))}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          activityForm.type === key ? 'bg-[#1B5E37] text-white border-[#1B5E37]' : 'border-border hover:bg-secondary'
                        }`}>
                        <Icon className="w-3 h-3" /> {cfg.label}
                      </button>
                    );
                  })}
                </div>
                <Textarea
                  placeholder={activityForm.type === 'call' ? 'Бележка от разговора...' : activityForm.type === 'meeting' ? 'Детайли от срещата...' : 'Бележка...'}
                  value={activityForm.content}
                  onChange={e => setActivityForm(f => ({ ...f, content: e.target.value }))}
                  rows={3}
                  autoFocus
                />
                {(activityForm.type === 'call' || activityForm.type === 'meeting') && (
                  <Select value={activityForm.outcome} onValueChange={v => setActivityForm(f => ({ ...f, outcome: v }))}>
                    <SelectTrigger><SelectValue placeholder="Резултат (по желание)" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(OUTCOMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex gap-2">
                  <Button onClick={addActivity} className="bg-[#1B5E37] hover:bg-[#154a2c]" disabled={!activityForm.content.trim()}>Запази</Button>
                  <Button variant="outline" onClick={() => setAddingActivity(false)}>Отказ</Button>
                </div>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-white border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#1B5E37]" /> История ({timeline.length})
            </h3>

            {timeline.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-8">Все още няма активности</p>
            ) : (
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-border" />
                <div className="space-y-4">
                  {timeline.map((item, idx) => {
                    if (item._type === 'inquiry') {
                      return (
                        <div key={item.id} className="flex gap-4 relative">
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-none z-10">
                            <MessageSquare className="w-4 h-4 text-orange-600" />
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-medium text-sm">Запитване от сайта</span>
                              <span className="text-xs text-muted-foreground">{timeAgo(item.created_at)}</span>
                            </div>
                            {item.property_name && (
                              <div className="flex items-center gap-1 text-xs text-[#1B5E37] mb-1">
                                <Building className="w-3 h-3" /> {item.property_name}
                              </div>
                            )}
                            <p className="text-sm text-muted-foreground bg-orange-50 p-3 rounded-lg">{item.message}</p>
                          </div>
                        </div>
                      );
                    }

                    const cfg = ACTIVITY_TYPES[item.type] || ACTIVITY_TYPES.note;
                    const Icon = cfg.icon;
                    return (
                      <div key={item.id} className="flex gap-4 relative group">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-none z-10 ${cfg.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <span className="font-medium text-sm">{cfg.label}</span>
                              {item.outcome && <span className="ml-2 text-xs text-muted-foreground">{OUTCOMES[item.outcome]}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{timeAgo(item.created_at)}</span>
                              <button onClick={() => deleteActivity(item.id)}
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground bg-secondary/30 p-3 rounded-lg">{item.content}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-muted-foreground">{item.created_by_name}</span>
                            {item.property_name && (
                              <span className="text-xs text-[#1B5E37] flex items-center gap-1">
                                <Building className="w-3 h-3" />{item.property_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
